import { ref } from 'vue';
import axios from 'axios';
import apiClient from '../api';

// Router instance will be set from main.js
let router = null;

// Function to set router instance
const setRouter = (routerInstance) => {
  router = routerInstance;
};

const isAuthenticated = ref(false);
const currentUser = ref(null);
const authInitialized = ref(false);

// Function to update authentication state
const updateAuthStatus = (status, user = null) => {
  isAuthenticated.value = status;
  currentUser.value = user;
};

// Function to get tokens from localStorage
const getTokens = () => {
  const accessToken = localStorage.getItem('accessToken');
  const refreshToken = localStorage.getItem('refreshToken');
  console.log('🔑 Getting tokens from localStorage:', { 
    hasAccessToken: !!accessToken, 
    hasRefreshToken: !!refreshToken,
    accessTokenLength: accessToken ? accessToken.length : 0
  });
  return {
    accessToken,
    refreshToken,
  };
};

// Function to set tokens in localStorage
const setTokens = (accessToken, refreshToken) => {
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
};

// Function to clear tokens from localStorage
const clearTokens = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
};

// Login function
const login = async (username, password) => {
  try {
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    const response = await apiClient.post('/auth/login', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    console.log('Login response:', response.data);
    if (response.data.data.access_token) {
      const { access_token, refresh_token } = response.data.data;
      setTokens(access_token, refresh_token);
      updateAuthStatus(true, response.data.user);
      
      // Use router.push for SPA navigation if available, otherwise fallback to window.location
      if (router) {
        await router.push('/dashboard');
      } else {
        window.location.href = '/dashboard';
      }
      return true;
    } else {
      updateAuthStatus(false);
      return false;
    }
  } catch (error) {
    updateAuthStatus(false);
    throw error;
  }
};

// Logout function
const logout = async () => {
  console.log('🚪 Logging out user...');
  
  try {
    // Try to notify server about logout, but don't fail if it doesn't work
    // Use axios directly to avoid interceptor loops
    const { accessToken } = getTokens();
    if (accessToken) {
      await axios.post(`${process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000/v1'}/auth/logout`, {}, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        },
        timeout: 5000 // 5 second timeout
      });
    }
  } catch (error) {
    console.error('Logout failed on server (continuing anyway):', error);
  } finally {
    // Always clear local state regardless of server response
    clearTokens();
    updateAuthStatus(false);
    
    console.log('🚪 Redirecting to login page...');
    
    // Use router.push for SPA navigation if available, otherwise fallback to window.location
    if (router) {
      await router.push('/login');
    } else {
      window.location.href = '/login';
    }
  }
};

// Initialize auth state on app load
const initAuth = async () => {
  console.log('🔄 Initializing auth...');
  const { accessToken } = getTokens();
  console.log('🔑 Access token exists:', !!accessToken);
  
  if (accessToken) {
    try {
      // The request interceptor will add the token
      console.log('📡 Checking auth status...');
      const response = await apiClient.get('/auth/status');
      console.log('✅ Auth status response:', response.data);
      
      if (response.data.logged_in) {
        updateAuthStatus(true, response.data.user);
        console.log('✅ User authenticated:', response.data.user.username);
      } else {
        updateAuthStatus(false);
        clearTokens(); // Clear invalid tokens
        console.log('❌ User not authenticated');
      }
    } catch (error) {
      console.error('❌ Auth status check failed:', error);
      updateAuthStatus(false);
      clearTokens(); // Clear invalid tokens
    }
  } else {
    updateAuthStatus(false);
    console.log('❌ No access token found');
  }
  
  authInitialized.value = true;
  console.log('✅ Auth initialization complete. Authenticated:', isAuthenticated.value);
};

export { isAuthenticated, currentUser, authInitialized, login, logout, initAuth, getTokens, setTokens, setRouter };